import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Observable, Subject, filter, map, mergeMap, takeUntil, tap } from 'rxjs'
import { CurrentOrganization } from '@shared/services/current-organization'
import { IFavoriteProject } from '@app/projects-module/core/data-types/favorite-projects.interface'
import {
  FavoriteProjectsStoreFacade,
  IProjectFavoritePosition,
} from '@app/projects-module/state/favorite-projects/favorite-projects-store.facade'
import { ACLService } from '@shared/services/acl'
import { TranslateService } from '@app/shared/services/translate'
import { SignOutService } from '@app/shared/services/sign-out'
import locales from './locales'
import { CurrentUser } from '@app/shared/services/current-user'
import { SimpleModalService } from '@app/shared/services/simple-modal'
import { SimpleModalConfirmationComponent } from '../simple-modal-confirmation'
import { createGuid } from '@app/shared/helpers/guid-generator.helper'
import { OrganizationsStoreFacade } from '@app/organizations-module/state/organizations-store.facade'
import { DefaultModuleTypes, IOrganization } from '@app/shared/data-types/organization.interface'
import { Optional } from '@app/shared/data-types/optional.types'
import { ActivatedRoute, Router, Scroll, IsActiveMatchOptions } from '@angular/router'
import { StreamUnreadCheckService } from '@app/shared/services/stream-unread-check/stream-unread-check.service'
import { ButtonType } from '../simple-modal-confirmation/simple-modal-confirmation.component'

enum SidebarMenuMode {
  TABS = 'tabs',
  SETTINGS = 'settings',
}

@Component({
  selector: 'sidebar-menu',
  templateUrl: './sidebar-menu.component.html',
  styleUrls: ['./sidebar-menu.component.scss'],
  standalone: false,
})
export class SidebarMenuComponent implements OnInit, OnDestroy {
  constructor(
    public currentOrganization: CurrentOrganization,
    public currentUser: CurrentUser,
    private translate: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private orgStoreFacade: OrganizationsStoreFacade,
    private favoriteProjectsStore: FavoriteProjectsStoreFacade,
    private ACL: ACLService,
    private signOutService: SignOutService,
    private simpleModalService: SimpleModalService,
    public streamUnreadCheckService: StreamUnreadCheckService
  ) {}

  t = this.translate.create(locales)

  destroy$ = new Subject<void>()
  isDefaultModuleSet = false
  wasInSettingsMode = false
  rlao: IsActiveMatchOptions = { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }

  currentOrg$: Observable<Optional<IOrganization>>
  favoriteProjects$: Observable<IFavoriteProject[]>
  canManageUsers$: Observable<boolean>
  canManageTeams$: Observable<boolean>
  isOrgOwner$: Observable<boolean>
  canManageRoles$: Observable<boolean>
  canManageProjectRoles$: Observable<boolean>
  canManageUsersForSettings$: Observable<boolean>
  canManageDepartments$: Observable<boolean>
  canManageFinances$: Observable<boolean>
  dialogId = createGuid()
  activeTab: DefaultModuleTypes | null
  defaultModuleTypes = DefaultModuleTypes
  defaultModule: DefaultModuleTypes
  activeMenuMode = SidebarMenuMode.TABS
  sidebarMenuMode = SidebarMenuMode

  get isActiveTabLetuchka(): boolean {
    return this.activeTab === DefaultModuleTypes.LETUCHKA
  }

  get isActiveTabScreen(): boolean {
    return this.activeTab === DefaultModuleTypes.SCREEN
  }

  get isTabsMode(): boolean {
    return this.activeMenuMode === SidebarMenuMode.TABS
  }

  get isSettingsMode(): boolean {
    return this.activeMenuMode === SidebarMenuMode.SETTINGS
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        // TODO: сделать отслеживание currentOrganization, сейчас срабатывает на каждое изменение роута
        // NavigationEnd нет после логина, поэтому instanceof Scroll
        filter((e) => e instanceof Scroll),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild && !route.snapshot.paramMap.get('organization_id')) {
            route = route.firstChild
          }
          return route
        }),
        filter((route) => route.outlet === 'primary'),

        mergeMap((route) => route.params),
        takeUntil(this.destroy$)
      )
      .subscribe(({ organization_id }) => {
        const currentOrgId = +organization_id

        this.currentOrg$ = this.orgStoreFacade.getOrganizationById(currentOrgId)
        this.canManageUsers$ = this.ACL.canManageUsers(currentOrgId)
        this.canManageTeams$ = this.ACL.canManageTeams(currentOrgId)
        this.canManageRoles$ = this.ACL.canManageOrganizationRoles(currentOrgId)
        this.canManageProjectRoles$ = this.ACL.canManageProjectRoles(currentOrgId)
        this.canManageDepartments$ = this.ACL.canManageDepartments(currentOrgId)
        this.canManageFinances$ = this.ACL.canManageFinances(currentOrgId)
        this.isOrgOwner$ = this.ACL.isOrgOwnerRole(currentOrgId)

        // Set active tab on init
        if (!this.isDefaultModuleSet) {
          this.activeTab = this.currentOrganization.default_module
          this.isDefaultModuleSet = true
        }

        this.orgStoreFacade
          .getOrganizationById(this.currentOrganization.id)
          .pipe(
            takeUntil(this.destroy$),
            filter(Boolean),
            tap((org) => {
              // change defaultModule on settings change
              this.defaultModule = org.default_module
            })
          )
          .subscribe()

        if (this.router.url.includes(`/org/${currentOrgId}/settings`)) {
          this.activeMenuMode = SidebarMenuMode.SETTINGS
          this.activeTab = null
          this.wasInSettingsMode = true
        } else {
          this.activeMenuMode = SidebarMenuMode.TABS
          // Определяем активный таб на основе текущего маршрута
          this.activeTab = this.determineActiveTabFromRoute()
        }
      })

    this.favoriteProjects$ = this.favoriteProjectsStore.getFavoriteProjects()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
  }

  private determineActiveTabFromRoute(): DefaultModuleTypes {
    if (this.router.url.includes('recorder')) {
      return DefaultModuleTypes.SCREEN
    }

    if (this.wasInSettingsMode) {
      this.wasInSettingsMode = false
      return this.defaultModule
    }

    return DefaultModuleTypes.LETUCHKA
  }

  isLibraryActive(): boolean {
    const pattern = /\/library(?:$|\/[a-f0-9-]{8,}(?:\/|$)|[?#]|$)/
    return pattern.test(this.router.url)
  }

  switchTab(tab: DefaultModuleTypes): void {
    this.activeMenuMode = SidebarMenuMode.TABS
    this.activeTab = tab

    switch (tab) {
      case DefaultModuleTypes.SCREEN:
        this.router.navigate(['recorder', 'org', this.currentOrganization.id, 'library'])
        break
      case DefaultModuleTypes.LETUCHKA:
        this.router.navigate(['org', this.currentOrganization.id, 'daily'])
        break
    }
  }

  openSettingsMenu(): void {
    this.activeMenuMode = SidebarMenuMode.SETTINGS
    this.activeTab = null
    this.router.navigate(['/org', this.currentOrganization.id, 'settings'])
  }

  updateFavoritePosition(data: { oldOrder: IProjectFavoritePosition[]; newOrder: IProjectFavoritePosition[] }): void {
    this.favoriteProjectsStore.updateProjectFavoritePosition(this.currentOrganization.id, data.newOrder, data.oldOrder)
  }

  toggle(): void {
    document.body.classList.toggle('body-navbar--collapsed')
  }

  handleSignOut(): void {
    const modal = this.simpleModalService.show<SimpleModalConfirmationComponent>(
      SimpleModalConfirmationComponent,
      this.dialogId,
      {
        title: this.t('signOut.title'),
        text: this.t('signOut.modal.text'),
        ok: this.t('signOut.modal.ok'),
        okType: ButtonType.DANGER,
      }
    )
    modal?.instance.onConfirm.subscribe(() => {
      this.signOutService.signOut()
      this.simpleModalService.hide(this.dialogId)
    })
    modal?.instance.onClose.subscribe(() => {
      this.simpleModalService.hide(this.dialogId)
    })
  }

  get mode() {
    return this.currentOrganization.team_mode ? 'teamMode.' : 'singleMode.'
  }
}
